import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, Share2 } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { API } from '../App';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  // Demo article for display
  const demoArticles = {
    'comment-creer-entreprise-senegal': {
      article_id: 'demo-1',
      title: 'Comment créer une entreprise au Sénégal : Guide complet',
      slug: 'comment-creer-entreprise-senegal',
      excerpt: 'Découvrez toutes les étapes pour formaliser votre entreprise au Sénégal.',
      content: `
## Introduction

Créer une entreprise au Sénégal est un parcours passionnant mais qui nécessite une bonne préparation. Ce guide vous accompagne à travers toutes les étapes essentielles.

## 1. Choisir la forme juridique

Le choix de la forme juridique est crucial et dépend de plusieurs facteurs :

- **Entreprise individuelle** : Idéale pour les petites activités
- **SARL** : Pour les projets de taille moyenne avec peu d'associés
- **SAS** : Plus flexible, adaptée aux startups et projets innovants
- **SA** : Pour les grands projets nécessitant des capitaux importants

## 2. Les étapes d'immatriculation

### 2.1 Rédaction des statuts
Les statuts définissent les règles de fonctionnement de votre entreprise. Ils doivent être rédigés avec soin et inclure :
- L'objet social
- Le capital social
- La répartition des parts
- Les modalités de gestion

### 2.2 Dépôt du capital
Le capital doit être déposé sur un compte bancaire bloqué au nom de la société en formation.

### 2.3 Formalités administratives
- Déclaration au RCCM (Registre du Commerce et du Crédit Mobilier)
- Obtention du NINEA (Numéro d'Identification National des Entreprises et Associations)
- Immatriculation fiscale

## 3. Les coûts à prévoir

Les frais de création varient selon la forme juridique :
- Frais de notaire
- Frais d'immatriculation
- Frais de publication légale

## Conclusion

La création d'entreprise au Sénégal est un processus qui peut être simplifié avec un bon accompagnement. Chez Teranga Global Legal, nous vous guidons à chaque étape avec notre méthode des 3F.
      `,
      category: 'Création d\'entreprise',
      image_url: 'https://images.unsplash.com/photo-1664575602554-2087b04935a5?w=1200',
      author_name: 'Cheikh Talibouya Camara NDIAYE',
      created_at: '2024-01-15',
      published: true
    },
    'erreurs-juridiques-entrepreneur': {
      article_id: 'demo-2',
      title: 'Les 5 erreurs juridiques à éviter en tant qu\'entrepreneur',
      slug: 'erreurs-juridiques-entrepreneur',
      excerpt: 'Protégez votre entreprise en évitant ces erreurs courantes.',
      content: `
## Introduction

De nombreux entrepreneurs commettent des erreurs juridiques qui peuvent avoir des conséquences graves pour leur activité. Voici les 5 erreurs les plus courantes à éviter.

## 1. Négliger la protection juridique dès le départ

Beaucoup d'entrepreneurs se lancent sans structurer juridiquement leur activité. Cette erreur peut coûter cher :
- Responsabilité personnelle illimitée
- Difficultés à lever des fonds
- Problèmes avec les associés

## 2. Ignorer les contrats écrits

"La parole donnée suffit" est un mythe dangereux en affaires :
- Toujours formaliser les accords par écrit
- Faire valider les contrats importants par un juriste
- Conserver tous les documents

## 3. Mal gérer la propriété intellectuelle

Votre marque, vos innovations, vos créations doivent être protégées :
- Déposer sa marque à l'OAPI
- Protéger ses brevets
- Sécuriser ses droits d'auteur

## 4. Sous-estimer la conformité réglementaire

Chaque secteur a ses règles spécifiques :
- Licences et autorisations
- Normes sanitaires et sécurité
- Obligations fiscales et sociales

## 5. Mélanger finances personnelles et professionnelles

Cette erreur compromet :
- La protection de votre patrimoine personnel
- La crédibilité auprès des banques
- La gestion comptable

## Conclusion

Un accompagnement juridique professionnel dès le début de votre aventure entrepreneuriale est un investissement, pas une dépense.
      `,
      category: 'Conseil juridique',
      image_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200',
      author_name: 'Cheikh Abdoul Aziz COULIBALY',
      created_at: '2024-01-10',
      published: true
    }
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(`${API}/articles/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setArticle(data);
        } else {
          // Use demo article if not found
          setArticle(demoArticles[slug] || null);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setArticle(demoArticles[slug] || null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center py-20">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Article non trouvé</h1>
          <p className="text-slate-600 mb-8">L'article que vous recherchez n'existe pas.</p>
          <Link to="/blog">
            <Button className="btn-primary" data-testid="back-to-blog">
              Retour au blog
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Image */}
      <section className="relative h-[400px] md:h-[500px]" data-testid="article-hero">
        <img
          src={article.image_url || 'https://images.unsplash.com/photo-1664575602554-2087b04935a5?w=1200'}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        
        {/* Back Button */}
        <div className="absolute top-8 left-0 right-0">
          <div className="max-w-4xl mx-auto px-6">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour au blog
            </Link>
          </div>
        </div>
        
        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-[#E6C200]" />
              <span className="text-[#E6C200] font-medium">{article.category}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white" data-testid="article-title">
              {article.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12" data-testid="article-content">
        <div className="max-w-4xl mx-auto px-6">
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">{article.author_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">{formatDate(article.created_at)}</span>
            </div>
            <button className="flex items-center gap-2 text-slate-600 hover:text-[#006F33] transition-colors ml-auto">
              <Share2 className="w-5 h-5" />
              Partager
            </button>
          </div>

          {/* Content */}
          <article className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-[#1A1A1A] prose-p:text-slate-600 prose-a:text-[#006F33] prose-strong:text-[#1A1A1A] prose-ul:text-slate-600 prose-li:text-slate-600">
            <div 
              dangerouslySetInnerHTML={{ 
                __html: article.content
                  .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-8 mb-4">$1</h2>')
                  .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-6 mb-3">$1</h3>')
                  .replace(/^\- \*\*(.*?)\*\*: (.*$)/gim, '<li><strong>$1</strong>: $2</li>')
                  .replace(/^\- (.*$)/gim, '<li>$1</li>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n\n/g, '</p><p class="mb-4">')
              }}
            />
          </article>

          {/* CTA */}
          <div className="mt-12 p-8 bg-[#006F33] rounded-2xl text-center" data-testid="article-cta">
            <h3 className="text-2xl font-bold text-white mb-4">
              Besoin d'accompagnement ?
            </h3>
            <p className="text-white/80 mb-6">
              Nos experts sont là pour vous aider dans votre projet entrepreneurial.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#006F33] hover:bg-slate-100" data-testid="article-contact-cta">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogArticlePage;
